// src/algorithms/ordered/blue-noise.ts
//
// Loads /blue-noise-256.png at runtime (one HTTP fetch on first use).
// If the asset is missing, falls back to a white-noise tile so the build
// keeps working. Quality will be visibly worse without the real tile.
//
// TODO: replace with real void-and-cluster tile (e.g. Christoph Peters,
// https://momentsingraphics.de/BlueNoise.html). Place at public/blue-noise-256.png.
import { whiteNoiseBytes } from './white-noise';

let cache: { tex: WebGLTexture; size: number } | null = null;
let warnedFallback = false;

const N = 256;

async function loadBlueNoiseImage(): Promise<HTMLImageElement | null> {
  try {
    const img = new Image();
    img.src = '/blue-noise-256.png';
    await img.decode();
    if (img.naturalWidth === 0) return null;
    return img;
  } catch {
    return null;
  }
}

export async function uploadBlueNoiseTexture(
  gl: WebGL2RenderingContext,
): Promise<{ tex: WebGLTexture; size: number }> {
  if (cache) return cache;

  const tex = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, tex);

  const img = await loadBlueNoiseImage();
  if (img) {
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, gl.RED, gl.UNSIGNED_BYTE, img);
  } else {
    if (!warnedFallback) {
      // eslint-disable-next-line no-console
      console.warn('[blue-noise] /blue-noise-256.png not found; falling back to white noise.');
      warnedFallback = true;
    }
    const bytes = whiteNoiseBytes(N);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, N, N, 0, gl.RED, gl.UNSIGNED_BYTE, bytes);
  }
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
  cache = { tex, size: N };
  return cache;
}
